<template>
  <div
    :style="{
      margin: '0 auto',
      position: 'relative',
      width: scaledCropWidth + 'px',
      height: scaledCropHeight + 'px',
      overflow: 'hidden'
    }"
  >
    <div
      :style="{
        position: 'absolute',
        left: -scaledCropX + 'px',
        top: -scaledCropY + 'px',
        width: scaledWidth + 'px',
        height: scaledHeight + 'px'
      }"
    >
      <edit-player
        ref="editPlayer"
        :player-data="value"
        :mode="statusMode"
        :src="src"
        :thumbnail="thumbnail"
        :is-muted.sync="isMuted"
        :video-sizes="videoSizes"
        :crop-video-resize="cropVideoResize"
        :player-time="playerTime"
        :player-start-time="playerStartTime"
        :paused="paused"
        :original-size="true"
        :hide-controls="true"
        @timeUpdate="$emit('timeUpdate', $event)"
        @updatePausedTrue="updatePauseTrue"
        @updatePausedFalse="updatePauseFalse"
        @playerDataChanged="playerDataChanged"
      />
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import VideoPlayer from "@/components/media/video/VideoPlayer.vue";
import { MODES_DRAG, MODES_PREVIEW } from "./cut_modes";
import EditPlayer from "@/components/media/editing/EditPlayer.vue";

// @group Editing
// Reactive player for edit panel
export default {
  name: "WrapperCropEditPlayer",
  components: {
    EditPlayer
  } /*
  model: {
    prop: "playerData",
    event: "playerDataChanged"
  },*/,
  props: {
    scale: {
      type: Number,
      default: 1
    },
    playerSize: {
      type: Object
    },
    cropX: {
      type: Number,
      default: 0
    },
    cropY: {
      type: Number,
      default: 0
    },
    cropWidth: {
      type: Number,
      default: 0
    },
    cropHeight: {
      type: Number,
      default: 0
    },
    statusMode: {
      type: String,
      default: "drag"
    },
    startPreviewEmitValue: {
      type: Number,
      default: 0
    },
    playerTime: {
      type: Number,
      default: 0
    },
    playerStartTime: {
      type: Number,
      default: 0
    },
    // Player data object
    value: {
      type: Object,
      required: true
    },
    paused: {
      type: Boolean,
      required: true
    },
    // Thumbnail link
    thumbnail: {
      type: String,
      default: null
    },
    // Source video link
    src: {
      type: [Object, String],
      required: true
    },
    // Synchronized muted flag
    isMuted: {
      type: Boolean,
      default: false
    },
    cropVideoResize: {
      type: Boolean,
      default: false
    },
    stopPreview: {
      type: Boolean,
      default: false
    },
    // Video width and heigth in pixels
    videoSizes: {
      type: Object,
      required: true,
      vaidator(val) {
        const { width, height } = val;
        return Number.isInteger(width) && Number.isInteger(height);
      }
    }
  },
  /** VideoJS player options */
  playerOptions: {
    /** Autoplay need to initialize player area at start,
     * otherwise crop/text/image areas will can go beyound
     * player boundary */
    autoplay: true,
    controls: true,
    fill: true,
    preload: "none",
    cors: true,
    children: {
      controlBar: {
        captionsButton: false,
        chaptersButton: false,
        fullscreenToggle: false,
        subtitlesButton: false,
        playbackRateMenuButton: false,
        pictureInPictureToggle: false
      }
    }
  },
  data() {
    return {
      playerOptions: {
        /** Autoplay need to initialize player area at start,
         * otherwise crop/text/image areas will can go beyound
         * player boundary */
        autoplay: true,
        controls: true,
        fill: true,
        preload: "none",
        cors: true,
        children: {
          controlBar: {
            captionsButton: false,
            chaptersButton: false,
            fullscreenToggle: false,
            subtitlesButton: false,
            playbackRateMenuButton: false,
            pictureInPictureToggle: false
          }
        }
      },
      stopPreviewWatch: null,
      time: 0,
      // paused: this.playerData.paused,
      loading: true,
      duration: 10,
      // Player size settings
      relativeMaxWidth: 0.8,
      relativeMaxHeight: 0.5,
      width: 800,
      height: 640,
      playerPaused: true
    };
  },
  computed: {
    cropPreviewScale: function() {
      if (this.cropHeight > this.cropWidth)
        return this.playerSize.height / this.cropHeight;
      else return this.playerSize.width / this.cropWidth;
    },
    realCropWidth: function() {
      return this.cropWidth * this.cropPreviewScale;
    },
    realCropHeight: function() {
      return this.cropHeight * this.cropPreviewScale;
    },
    realCropX: function() {
      return this.cropX * this.cropPreviewScale;
    },
    realCropY: function() {
      return this.cropY * this.cropPreviewScale;
    },
    realWidth: function() {
      return this.playerSize.width * this.cropPreviewScale;
    },
    realHeight: function() {
      return this.playerSize.height * this.cropPreviewScale;
    },
    viewportScale: function(){
      return Math.min(
        1,
        this.playerSize.width / this.realCropWidth,
        this.playerSize.height / this.realCropHeight
      );
    },
    scaledCropWidth: function() {
      return this.realCropWidth * this.viewportScale;
    },
    scaledCropHeight: function() {
      return this.realCropHeight * this.viewportScale;
    },
    scaledCropX: function() {
      return this.realCropX * this.viewportScale;
    },
    scaledCropY: function() {
      return this.realCropY * this.viewportScale;
    },
    scaledWidth: function() {
      return this.realWidth * this.viewportScale;
    },
    scaledHeight: function() {
      return this.realHeight * this.viewportScale;
    },
  },
  methods: {
    updatePauseTrue(e) {
      this.$emit("updatePausedTrue", e);
    },
    updatePauseFalse(e) {
      this.$emit("updatePausedFalse", e);
    },
    setTime(e) {
      this.$refs.editPlayer.setTime(e);
    },
    playerDataChanged(e) {
      this.$emit("input", e);
    }
  }
};
</script>

<style scoped>
.edit-form-player {
  height: 100%;
  max-width: 100%;
}
</style>

<style>
.edit-form-player .vjs-tech {
  /* width: auto !important; */
  width: 100%;
  height: 100%;
}
</style>
